/**
 * Formatacao para a tela.
 *
 * AUSENCIA VIRA TRAVESSAO, NUNCA ZERO (LEI 2). Um painel que mostra "0
 * contratos vencendo" quando na verdade nao conseguiu medir manda o gestor
 * para casa tranquilo. Travessao diz a verdade: nao ha o que contar aqui.
 *
 * DINHEIRO CHEGA COMO TEXTO da API — `Decimal` serializado como ponto
 * flutuante perde centavo. A conversao para numero acontece SO na formatacao,
 * onde o unico destino e a tela, e nunca antes de um calculo.
 */

const TRAVESSAO = "—"

export function moeda(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return TRAVESSAO
  const numero = Number(valor)
  if (!Number.isFinite(numero)) return TRAVESSAO
  return numero.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  })
}

export function moedaCurta(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return TRAVESSAO
  const numero = Number(valor)
  if (!Number.isFinite(numero)) return TRAVESSAO
  if (Math.abs(numero) >= 1_000_000) {
    return `R$ ${(numero / 1_000_000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })} mi`
  }
  if (Math.abs(numero) >= 1_000) {
    return `R$ ${(numero / 1_000).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    })} mil`
  }
  return moeda(valor)
}

export function inteiro(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return TRAVESSAO
  return valor.toLocaleString("pt-BR")
}

export function percentual(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return TRAVESSAO
  const numero = Number(valor)
  if (!Number.isFinite(numero)) return TRAVESSAO
  return `${numero.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`
}

export function data(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return TRAVESSAO
  // A API manda `AAAA-MM-DD`. Montar a data com `new Date(texto)` aplicaria
  // fuso e trocaria o dia para quem esta a oeste de Greenwich — um contrato
  // que vence dia 1 apareceria vencendo dia 30 do mes anterior.
  const partes = valor.slice(0, 10).split("-")
  if (partes.length !== 3) return TRAVESSAO
  return `${partes[2]}/${partes[1]}/${partes[0]}`
}

export function quantidade(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return TRAVESSAO
  const numero = Number(valor)
  if (!Number.isFinite(numero)) return TRAVESSAO
  return numero.toLocaleString("pt-BR", { maximumFractionDigits: 4 })
}

/**
 * O PROCESSO ADMINISTRATIVO — sempre com NUMERO E ANO, num lugar so.
 *
 * DEV, em 04/09/2026: *"onde tiver o nome do processo, ele estar acompanhado
 * com seu numero e ANO, igual vem do ATHUS"*.
 *
 * No ATHUS o processo e `numero_processo/cd_ano`, e o banco daqui ja guarda
 * assim — medido em 04/09: **237 contratos, ZERO sem o ano**, nas duas colunas
 * (`processo_administrativo` e `origem_processo`). O defeito era de TELA: a
 * esteira se intitulava "Processo administrativo", sem dizer qual; a trilha
 * dizia so "Processo".
 *
 * O "nº" faz parte da formatacao e nao do dado: "2457/2026" sozinho, ao
 * lado de "94/2026", vira dois pares de numeros que o olho troca. O ordinal
 * diz qual dos dois e o processo.
 *
 * SEM PROCESSO VIRA TRAVESSAO (LEI 2), e nao "administrativo" nem cadeia
 * vazia: contrato proprio, nao vindo do ATHUS, pode nao ter processo — sao 32
 * dos 237, medidos —, e inventar a palavra generica no lugar do numero faz a
 * tela parecer que o dado existe.
 */
export function processo(valor: string | null | undefined): string {
  // SEM NÚMERO, E NÃO "—" (spec 0076): "Processo —" na trilha parecia um
  // título quebrado.
  if (valor === null || valor === undefined || valor.trim() === "") {
    return "sem número"
  }
  return `nº ${valor.trim()}`
}

/** O número do processo de um contrato: o do próprio contrato vale primeiro, e
 *  o da origem (ATHUS) entra na falta dele. Mesmo critério do servidor na
 *  Esteira e do PDF do laudo (spec 0076). */
export function processoDoContrato(
  administrativo: string | null | undefined,
  daOrigem: string | null | undefined,
): string {
  const doContrato = administrativo?.trim() ?? ""
  return processo(doContrato !== "" ? doContrato : daOrigem)
}

/** Vocabulario da tela. O banco guarda o codigo; o gestor le a palavra. */
const ROTULOS: Record<string, string> = {
  minuta: "Minuta",
  assinado: "Assinado",
  vigente: "Vigente",
  suspenso: "Suspenso",
  encerrado: "Encerrado",
  rescindido: "Rescindido",
  pregao: "Pregão",
  concorrencia: "Concorrência",
  concurso: "Concurso",
  leilao: "Leilão",
  dialogo_competitivo: "Diálogo competitivo",
  dispensa: "Dispensa",
  inexigibilidade: "Inexigibilidade",
  servico_continuo: "Serviço contínuo",
  servico_escopo: "Serviço por escopo",
  obra: "Obra",
  reforma: "Reforma",
  fornecimento: "Fornecimento",
  fornecimento_continuo: "Fornecimento contínuo",
  aberta: "Aberta",
  apurada: "Apurada",
  atestada: "Atestada",
  rejeitada: "Rejeitada",
  previsto: "Previsto",
  liquidado: "Liquidado",
  pago: "Pago",
  cancelado: "Cancelado",
  acrescimo: "Acréscimo",
  supressao: "Supressão",
  prorrogacao: "Prorrogação",
  alteracao_qualitativa: "Alteração qualitativa",
  repactuacao: "Repactuação",
  reajuste: "Reajuste",
  correcao_monetaria: "Correção monetária",
  alteracao_de_dotacao: "Alteração de dotação",
  outra: "Outra",
  gestor: "Gestor",
  fiscal_titular: "Fiscal titular",
  fiscal_substituto: "Fiscal substituto",
  caucao: "Caução",
  seguro_garantia: "Seguro-garantia",
  fianca_bancaria: "Fiança bancária",
  titulo_de_capitalizacao: "Título de capitalização",
  ordinario: "Ordinário",
  estimativo: "Estimativo",
  global: "Global",
  informacao: "Informação",
  leve: "Leve",
  media: "Média",
  grave: "Grave",
  // A NATUREZA DO OBJETO, que a ficha passou a mostrar em bloco proprio na
  // F35. `servico` sem acento chegava cru na tela — codigo de banco lido por
  // quem nao programa. `fornecimento` ja esta acima, e serve aos dois campos.
  servico: "Serviço",
  // A gravidade da ocorrencia: `media` ja esta acima e vale para as duas.
  baixa: "Baixa",
  alta: "Alta",
  critica: "Crítica",
}

// Texto que o CSS não alcança (rótulo de gráfico SVG, texto dentro de botão
// flex): a primeira letra da frase vai maiúscula no próprio texto.
export function maiusculaInicial(texto: string): string {
  return texto.charAt(0).toLocaleUpperCase("pt-BR") + texto.slice(1) // anti-ia-ok: maiúscula da frase, não monograma
}

export function rotulo(codigo: string | null | undefined): string {
  if (codigo === null || codigo === undefined || codigo === "") return TRAVESSAO
  // Codigo sem rotulo aparece como esta, e nao em branco: ver o codigo cru na
  // tela e feio, mas some-lo esconde que falta traduzir.
  return ROTULOS[codigo] ?? codigo
}

export function documento(valor: string): string {
  const d = valor.replace(/\D/g, "")
  // O MARCADOR do contratado não identificado tem documento todo em zero. Um
  // "00.000.000/0000-00" na ficha parece CNPJ de verdade; não há documento (F45).
  if (d !== "" && /^0+$/.test(d)) return TRAVESSAO
  if (d.length === 14) {
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
  }
  if (d.length === 11) {
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
  }
  return valor
}

/* `classeDaSituacao` MORREU EM 04/09/2026, e o lugar dela fica marcado.
 *
 * Ela devolvia o nome de uma classe CSS a partir de uma situacao — uma
 * funcao de formatacao decidindo aparencia, e decidindo-a por uma escada de
 * `if` que ninguem lia ao escrever a tela. Quando o selo mudou de forma
 * (icone + fundo palido), ela teria de mudar junto, e nada obrigava isso.
 *
 * Agora a situacao vira uma CHAVE no mapa `SITUACOES` de `componentes/Selo`,
 * onde rotulo, tom e icone moram juntos. Formatar texto continua aqui;
 * escolher desenho e do componente. */

export { TRAVESSAO }

/** Data e hora de um carimbo ISO, no fuso de quem le: "02/09/2026 09:41". */
export function dataHora(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return "—"
  const quando = new Date(valor)
  if (Number.isNaN(quando.getTime())) return "—"
  const dois = (n: number) => String(n).padStart(2, "0")
  return (
    `${dois(quando.getDate())}/${dois(quando.getMonth() + 1)}/${quando.getFullYear()} ` +
    `${dois(quando.getHours())}:${dois(quando.getMinutes())}`
  )
}

/** SO O DIA de um carimbo ISO, no fuso de quem le: "02/09/2026".
 *
 * Existe separado de `data()` porque `data()` corta os dez primeiros
 * caracteres do texto e ignora o fuso — certo para `AAAA-MM-DD` (um contrato
 * que vence dia 1 nao pode aparecer vencendo dia 30), errado para carimbo com
 * hora. A esteira agrupa por dia e mostra a hora ao lado: se as duas coisas
 * saissem de fusos diferentes, o grupo "03/09" mostraria uma peca das 21h do
 * dia 02. */
export function dia(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return TRAVESSAO
  const quando = new Date(valor)
  if (Number.isNaN(quando.getTime())) return TRAVESSAO
  const dois = (n: number) => String(n).padStart(2, "0")
  return `${dois(quando.getDate())}/${dois(quando.getMonth() + 1)}/${quando.getFullYear()}`
}

/** SO A HORA de um carimbo ISO: "09:41". */
export function hora(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return TRAVESSAO
  const quando = new Date(valor)
  if (Number.isNaN(quando.getTime())) return TRAVESSAO
  const dois = (n: number) => String(n).padStart(2, "0")
  return `${dois(quando.getHours())}:${dois(quando.getMinutes())}`
}

/** Tamanho de arquivo. Base 1024, como o sistema operacional conta — o disco
 *  do servidor e o navegador contam assim, e mostrar base 1000 faria o numero
 *  da tela nao bater com o do arquivo salvo. */
export function tamanho(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return TRAVESSAO
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} KB`
  return `${(kb / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`
}


/**
 * O NIVEL DA ASSINATURA em portugues.
 *
 * O valor guardado e `avancada_interna` ou `qualificada_no_processo`, porque
 * ele e chave de parametro e viaja em `.env` e em tabela. A tela nao mostra
 * chave: quem le a conferencia de autenticidade tem um papel na mao e nao
 * trabalha neste sistema.
 *
 * Nivel desconhecido aparece COMO VEIO, e nao como travessao: um valor novo
 * que o servidor passou a devolver e informacao, e escondê-lo faria a tela
 * mentir por omissao (LEI 2).
 */
export function nivelDeAssinatura(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return TRAVESSAO
  if (valor === "avancada_interna") {
    return "avançada, assinada neste sistema"
  }
  if (valor === "qualificada_no_processo") {
    return "qualificada, com certificado no sistema de processo"
  }
  return valor
}

/* --- CPF (spec 0073: entra-se pelo CPF) ------------------------------------ */

/** Só os algarismos do CPF, no máximo 11. */
export function algarismosDoCpf(valor: string): string {
  return valor.replace(/\D/g, "").slice(0, 11)
}

/** A máscara `000.000.000-00`, posta enquanto a pessoa digita. */
export function mascaraDeCpf(valor: string): string {
  const d = algarismosDoCpf(valor)
  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

/** Os dígitos verificadores conferem? A mesma conta do servidor
 *  (`services/identificacao.cpf_valido`): onze algarismos iguais não são CPF. */
export function cpfValido(valor: string): boolean {
  const d = algarismosDoCpf(valor)
  if (d.length !== 11 || new Set(d).size === 1) return false
  const numeros = [...d].map(Number)
  for (const tamanho of [9, 10]) {
    const soma = numeros.slice(0, tamanho).reduce((total, n, i) => total + n * (tamanho + 1 - i), 0)
    if ((soma * 10) % 11 % 10 !== numeros[tamanho]) return false
  }
  return true
}

/** O contrato como as telas o mostram: "002/2025". O ano não se repete
 *  quando o número já o traz como segmento ("157/2026/PGM/PMCJ") — spec 0098. */
export function numeroComAno(numero: string, ano: number | null | undefined): string {
  const limpo = (numero ?? "").trim()
  if (ano === null || ano === undefined) return limpo
  if (limpo.split("/").slice(1).some((parte) => /^(19|20)\d{2}$/.test(parte.trim()))) return limpo
  return `${limpo}/${ano}`
}

/* --- CNPJ, CEP e UF (spec 0105) ---------------------------------------------- */

/** Só letras e algarismos do CNPJ, em maiúsculas, no máximo 14: desde 2026 as
 *  doze primeiras posições podem ter letra (CNPJ alfanumérico). */
export function algarismosDoCnpj(valor: string): string {
  return valor
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, 14)
}

/** A máscara `00.000.000/0000-00`, posta enquanto a pessoa digita. */
export function mascaraDeCnpj(valor: string): string {
  const d = algarismosDoCnpj(valor)
  if (d.length <= 2) return d
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
}

function digitoDoCnpj(valores: number[], pesos: number[]): number {
  const resto = valores.reduce((soma, valor, i) => soma + valor * (pesos[i] ?? 0), 0) % 11
  return resto < 2 ? 0 : 11 - resto
}

/** Os dígitos verificadores conferem? A mesma conta do servidor
 *  (`services/identificacao.cnpj_valido`): cada caractere vale o código ASCII
 *  menos 48, e catorze caracteres iguais não são CNPJ. */
export function cnpjValido(valor: string): boolean {
  const d = algarismosDoCnpj(valor)
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(d) || new Set(d).size === 1) return false
  const valores = [...d].map((c) => c.charCodeAt(0) - 48)
  const pesos = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  const primeiro = digitoDoCnpj(valores.slice(0, 12), pesos)
  const segundo = digitoDoCnpj([...valores.slice(0, 12), primeiro], [6, ...pesos])
  return valores[12] === primeiro && valores[13] === segundo
}

/** A máscara `00000-000`. */
export function mascaraDeCep(valor: string): string {
  const d = valor.replace(/\D/g, "").slice(0, 8)
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`
}

const ESTADOS: [string, string][] = [
  ["AC", "Acre"],
  ["AL", "Alagoas"],
  ["AP", "Amapá"],
  ["AM", "Amazonas"],
  ["BA", "Bahia"],
  ["CE", "Ceará"],
  ["DF", "Distrito Federal"],
  ["ES", "Espírito Santo"],
  ["GO", "Goiás"],
  ["MA", "Maranhão"],
  ["MT", "Mato Grosso"],
  ["MS", "Mato Grosso do Sul"],
  ["MG", "Minas Gerais"],
  ["PA", "Pará"],
  ["PB", "Paraíba"],
  ["PR", "Paraná"],
  ["PE", "Pernambuco"],
  ["PI", "Piauí"],
  ["RJ", "Rio de Janeiro"],
  ["RN", "Rio Grande do Norte"],
  ["RS", "Rio Grande do Sul"],
  ["RO", "Rondônia"],
  ["RR", "Roraima"],
  ["SC", "Santa Catarina"],
  ["SP", "São Paulo"],
  ["SE", "Sergipe"],
  ["TO", "Tocantins"],
]

/** As 27 unidades da federação, para a escolha da UF. */
export const UFS: { chave: string; rotulo: string }[] = ESTADOS.map(([sigla, nome]) => ({
  chave: sigla,
  rotulo: `${sigla} — ${nome}`,
}))

/** CNPJ ou CPF no mesmo campo: até onze algarismos, a máscara do CPF; com
 *  mais, ou com letra (CNPJ alfanumérico), a do CNPJ. */
export function mascaraDeDocumento(valor: string): string {
  const d = algarismosDoCnpj(valor)
  return d.length <= 11 && /^\d*$/.test(d) ? mascaraDeCpf(d) : mascaraDeCnpj(d)
}

export function plural(quantidade: number, singular: string, varios: string): string {
  return `${inteiro(quantidade)} ${quantidade === 1 ? singular : varios}`
}
